import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Grid, Container, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import bggrid from '../images/grid.png';

const FeaturesTimeline = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Features data for easy management
  const features = [
    {
      title: "Kubernetes Infrastructure",
      description: "Pre-configured clusters for development, staging, and production — deployed on AWS, GCP, or Azure.",
      position: "left" // Title on left, description on right
    },
    {
      title: "CI/CD Pipelines",
      description: "GitHub/GitLab-integrated pipelines with automation, rollback, and security scanning built-in.",
      position: "right" // Title on right, description on left
    },
    {
      title: "Security by Design",
      description: "Hardened infrastructure with secret management, RBAC, compliance-ready policies, and DevSecOps practices.",
      position: "left"
    },
    {
      title: "Expert-Led Support",
      description: "Hands-on support — including troubleshooting, upgrades, and maintenance.",
      position: "right"
    }
  ];

  // Component for a single feature with animations
  const FeatureItem = ({ feature, index, isMobile }) => {
    const [isVisible, setIsVisible] = useState(false);
    const featureRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          // When feature comes into view
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Once animation is triggered, we can stop observing
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.3 } // Trigger when 30% of the element is visible
      );

      if (featureRef.current) {
        observer.observe(featureRef.current);
      }

      return () => {
        if (featureRef.current) {
          observer.unobserve(featureRef.current);
        }
      };
    }, []);

    // Mobile view with animations
    if (isMobile) {
      return (
        <Box 
          ref={featureRef}
          sx={{ position: 'relative', mb: 10, pl: 5 ,}}
        >
          {/* Yellow dot with animation */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: isVisible ? 1 : 0, opacity: isVisible ? 1 : 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: '20px',
                top: '24px',
                width: '16px',
                height: '16px',
                bgcolor: '#fad32e',
                borderRadius: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
                boxShadow: '0 0 15px 5px rgba(250, 211, 46, 0.3)',
              }}
            />
          </motion.div>
          
          {/* Title with animation */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -30 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Typography 
              variant="h6" 
              sx={{
                color: '#FFFFFF',
                fontWeight: 'medium',
                fontSize: '1.1rem',
                mb: 3,
              }}
            >
              {feature.title}
            </Typography>
          </motion.div>
          
          {/* Feature box with animation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Box
              sx={{
                bgcolor: '#fad32e',
                borderRadius: '24px',
                p: 3,
                color: '#000000',
                mr: 2,
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1rem', fontFamily: "Poppins"}}>
                {feature.description}
              </Typography> 
            </Box>
          </motion.div>
        </Box>
      );
    }

    // Desktop view with animations
    return (
      <Grid 
        container 
        alignItems="center" 
        justifyContent="center" 
        sx={{ position: 'relative', width: '100%', mb: 15 }}
        ref={featureRef}
      >
        {/* Left side */}
        <Grid item md={5} sx={{ 
          pr: feature.position === "left" ? { md: 4 } : 0,
          pl: feature.position === "right" ? { md: 4 } : 0,
        }}>
          {feature.position === "left" ? (
            // Title on left with animation
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -50 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#FFFFFF',
                    fontWeight: 'medium',
                    fontSize: '1.25rem',
                    textAlign: 'right',
                    pr: { xs: 20, md: 20 }
                  }}
                >
                  {feature.title}
                </Typography>
              </Box>
            </motion.div>
          ) : (
            // Description on left with animation
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -50 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <Box
                sx={{
                  bgcolor: '#fad32e',
                  borderRadius: '24px',
                  p: 3,
                  color: '#000000',
                  maxWidth: '300px',
                  textAlign: 'center',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 2,
                  ml: 'auto', // Push to the right side
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '1rem' }}>
                  {feature.description}
                </Typography>
              </Box>
            </motion.div>
          )}
        </Grid>
        
        {/* Center space for timeline */}
        <Grid item md={2}></Grid>
        
        {/* Right side */}
        <Grid item md={5} sx={{ 
          pl: feature.position === "left" ? { md: 4 } : 0,
          pr: feature.position === "right" ? { md: 4 } : 0,
        }}>
          {feature.position === "right" ? (
            // Title on right with animation
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : 50 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-start',
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#FFFFFF',
                    fontWeight: 'medium',
                    fontSize: '1.25rem',
                    textAlign: 'left',
                    pl: { xs: 20, md: 20 }
                  }}
                >
                  {feature.title}
                </Typography>
              </Box>
            </motion.div>
          ) : (
            // Description on right with animation
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : 50 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <Box
                sx={{
                  bgcolor: '#fad32e',
                  borderRadius: '24px',
                  p: 3,
                  color: '#000000',
                  maxWidth: '300px',
                  textAlign: 'center',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '1rem' }}>
                  {feature.description}
                </Typography>
              </Box>
            </motion.div>
          )}
        </Grid>
        
        {/* Yellow dot with animation */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: isVisible ? 1 : 0, opacity: isVisible ? 1 : 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '20px',
              height: '20px',
              bgcolor: '#fad32e',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              boxShadow: '0 0 15px 5px rgba(250, 211, 46, 0.3)',
            }}
          />
        </motion.div>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: '#000000',
        backgroundImage: `url(${bggrid})`,
        backgroundSize: 'cover',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {/* Yellow glow effect behind heading */}
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '200px', md: '300px' },
            height: { xs: '200px', md: '300px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(250, 211, 46, 0.2) 0%, rgba(255, 255, 255, 0) 70%)',
            zIndex: 0,
          }}
        />

        {/* Heading with animation */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: '#fad32e',
              fontWeight: 'bold',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              mb: { xs: 8, md: 15 },
              position: 'relative',
              zIndex: 1,
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            What You Get with Hashinfra?
          </Typography>
        </motion.div>

        {/* Timeline */}
        <Box
          sx={{
            position: 'relative',
            maxWidth: '1300px',
            mx: 'auto',
            px: { xs: 2, md: 4 },
            pt: 5,
          }}
        >
          {/* Desktop center line */}
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: '4px',
                background: 'linear-gradient(to bottom, #fad32e 0%, rgba(250, 211, 46, 0.1) 100%)',
                transform: 'translateX(-50%)',
                zIndex: 0,
                boxShadow: '0 0 20px 5px rgba(250, 211, 46, 0.2)',
              }}
            />
          )}

          {/* Mobile left-aligned vertical line */}
          {isMobile && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '20px',
                width: '3px',
                background: 'linear-gradient(to bottom, #fad32e 0%, rgba(250, 211, 46, 0.1) 100%)',
                zIndex: 0,
                boxShadow: '0 0 15px 5px rgba(250, 211, 46, 0.2)',
              }}
            />
          )}

          {/* Features with animations */}
          {features.map((feature, index) => (
            <FeatureItem 
              key={index} 
              feature={feature} 
              index={index} 
              isMobile={isMobile} 
            />
          ))}
          
          {/* Optional: Add a CTA button at the end with animation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: { xs: 5, md: 10 },
                position: 'relative',
                zIndex: 2 
              }}
            >
              
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesTimeline;

